import type { UnitReport } from '@keetcheck/shared';

/** Report periods admins pick from, in local dates (inclusive). */
export type PresetKey = 'this-month' | 'last-month' | 'this-quarter' | 'last-quarter' | 'this-fy' | 'last-fy';

export const PRESETS: { key: PresetKey; label: string }[] = [
  { key: 'this-month', label: 'This month' },
  { key: 'last-month', label: 'Last month' },
  { key: 'this-quarter', label: 'This quarter' },
  { key: 'last-quarter', label: 'Last quarter' },
  { key: 'this-fy', label: 'This financial year' },
  { key: 'last-fy', label: 'Last financial year' },
];

const ymd = (y: number, m: number, d: number) => {
  const dt = new Date(Date.UTC(y, m, d));
  return dt.toISOString().slice(0, 10);
};

/** [from, to] for a preset, as of `today` (local). Financial years run July–June. */
export function presetRange(key: PresetKey, today = new Date()): [string, string] {
  const y = today.getFullYear(), m = today.getMonth();
  const q = Math.floor(m / 3);
  const fyStart = m >= 6 ? y : y - 1;
  switch (key) {
    case 'this-month': return [ymd(y, m, 1), ymd(y, m + 1, 0)];
    case 'last-month': return [ymd(y, m - 1, 1), ymd(y, m, 0)];
    case 'this-quarter': return [ymd(y, q * 3, 1), ymd(y, q * 3 + 3, 0)];
    case 'last-quarter': return [ymd(y, q * 3 - 3, 1), ymd(y, q * 3, 0)];
    case 'this-fy': return [ymd(fyStart, 6, 1), ymd(fyStart + 1, 6, 0)];
    case 'last-fy': return [ymd(fyStart - 1, 6, 1), ymd(fyStart, 6, 0)];
  }
}

export function periodLabel(from: string, to: string): string {
  const f = (s: string) => new Date(`${s}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return from === to ? f(from) : `${f(from)} – ${f(to)}`;
}

/** The whole report as one CSV (sections one after another), for Excel. */
export function reportCsv(r: UnitReport): string {
  const rows: (string | number | null)[][] = [];
  const add = (title: string, head: string[], body: (string | number | null)[][]) => {
    rows.push([title], head, ...body, []);
  };
  rows.push([`${r.orgName} report`, periodLabel(r.from, r.to)], []);
  add('Scheduled checks', ['Team', 'Due', 'On time', 'Late', 'Missed', 'Still open'],
    r.checks.byTeam.map((t) => [t.name, t.scheduled, t.onTime, t.late, t.missed, t.open]));
  add('Scheduled checks by vehicle', ['Vehicle', 'Due', 'On time', 'Late', 'Missed', 'Still open'],
    r.checks.byAsset.map((t) => [t.name, t.scheduled, t.onTime, t.late, t.missed, t.open]));
  add('Faults by vehicle', ['Vehicle', 'Found', 'Fixed', 'Open'], r.faults.byAsset.map((a) => [a.name, a.opened, a.fixed, a.openNow]));
  add('Oldest open faults', ['Fault', 'Vehicle', 'Days open', 'Assigned to'], r.faults.oldest.map((f) => [f.title, f.asset, f.days, f.assignedTo]));
  add('Off run', ['Vehicle', 'Days', 'Reasons', 'Still off run'], r.offRun.map((o) => [o.asset, o.days, o.reasons.join('; '), o.stillOff ? 'yes' : 'no']));
  add('Duty crew', ['Team', 'Shifts', 'Short of 3', 'Any warning'], r.crews.byTeam.map((t) => [t.name, t.shifts, t.short, t.warned]));
  add('Cover', ['Requests', 'Fully covered', 'Partly', 'Not covered', 'Hours asked', 'Hours uncovered'],
    [[r.cover.requests, r.cover.covered, r.cover.partly, r.cover.uncovered, r.cover.hoursAsked, r.cover.hoursUncovered]]);
  add('Who covered', ['Member', 'Times', 'Hours'], r.cover.topCoverers.map((c) => [c.name, c.times, c.hours]));
  add('Ops support', ['Member', 'Weeknights', 'Weekend nights', 'Day shifts'], r.ops.byMember.map((m) => [m.name, m.weeknight, m.weekendNight, m.day]));
  add('On leave', ['Member', 'From', 'Until'], r.people.onLeave.map((l) => [l.name, l.from, l.until ?? 'further notice']));
  add('Qualifications expired or expiring', ['Member', 'Qualification', 'Expires', 'Expired'], r.people.quals.map((q) => [q.name, q.code, q.expires, q.expired ? 'yes' : 'no']));
  const cell = (v: string | number | null) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return '﻿' + rows.map((r) => r.map(cell).join(',')).join('\r\n');
}
